import React, { memo } from 'react';
import { TouchableOpacity } from 'react-native';
import { Link } from 'expo-router';
import { Member } from '@/models/Member';
import { HStack } from '@/components/gluestack/hstack';
import { VStack } from '@/components/gluestack/vstack';
import { Text } from '@/components/gluestack/text';
import { ProfilePicture } from '@/components/domain/common/ProfilePicture';
import { PresenceIndicator } from '@/components/domain/members/PresenceIndicator';
import { CountryFlagIcon } from '@/components/ui/CountryFlagIcon';
import { FollowButton } from '@/components/domain/common/FollowButton';
import { UnblockButton } from '@/components/domain/common/UnblockButton';
import { RemoveUnlockButton } from '@/components/domain/common/RemoveUnlockButton';
import { Skeleton } from '@/components/gluestack/skeleton';
import { Tooltip, TooltipContent, TooltipText } from '@/components/gluestack/tooltip';
import { getCountryName } from '@/hooks/useCountryName';
import { getMemberPath } from '@/constants/routes';

interface MemberListCardProps {
  member?: Member;
  actionMode?: 'follow' | 'unblock' | 'remove-unlock' | 'none';
}

export const MemberListCard = memo(function MemberListCard({ member, actionMode = 'follow' }: MemberListCardProps) {

  if (!member) {
    return <MemberListCardSkeleton />;
  }

  return (
    <HStack className="items-start gap-1">
      <ProfilePicture member={member} showTooltip showPresence={false} />
      <VStack className="ml-3 flex-1 gap-1">
        <HStack className="flex-1 items-baseline">
          <Link href={getMemberPath(member)} asChild>
            <TouchableOpacity activeOpacity={0.8} className="shrink-0">
              <Text className="font-bold web:hover:underline">{member.UserName}</Text>
            </TouchableOpacity>
          </Link>
          {member.FirstNameCleaned ? (
            <>
              <Text className="font-normal"> </Text>
              <Text isTruncated className="min-w-0 shrink font-normal">({member.FirstNameCleaned})</Text>
            </>
          ) : null}
          <PresenceIndicator member={member} showLastSeen className="ml-2 self-center" />
        </HStack>
        {member.PlaceNameShortenedTwoElements ? (
          <HStack className="flex-1 items-center gap-2">
            {member.CountryCode ? (
              <Tooltip
                trigger={(triggerProps) => (
                  <CountryFlagIcon countryCode={member.CountryCode} size={16} {...triggerProps} />
                )}
              >
                <TooltipContent>
                  <TooltipText>{getCountryName(member.CountryCode)}</TooltipText>
                </TooltipContent>
              </Tooltip>
            ) : null}
            <Text
              size="sm"
              isTruncated
              className="flex-1 leading-tight text-typography-500">
              {member.PlaceNameShortenedTwoElements}
            </Text>
          </HStack>
        ) : null}
      </VStack>

      {actionMode === 'unblock' ? (
        <UnblockButton member={member} className="shrink-0 ml-1" hideIcon={true} cacheQueryKeyPrefix={'members'} />
      ) : actionMode === 'remove-unlock' ? (
        <RemoveUnlockButton member={member} className="shrink-0 ml-1" hideIcon={true} cacheQueryKeyPrefix={'members'} />
      ) : actionMode === 'follow' ? (
        <FollowButton member={member} className="shrink-0 ml-1" hideIcon={true} />
      ) : null}
    </HStack>
  );
});

function MemberListCardSkeleton() {
  return (
    <HStack className="mr-4 items-center gap-3">
      <Skeleton className="h-12 w-12 rounded-full" />
      <VStack className="ml-3 min-w-0 flex-1 gap-2">
        <Skeleton className="h-5 w-24 rounded-md" />
        <Skeleton className="h-4 w-16 rounded-md" />
      </VStack>
      <Skeleton className="h-9 w-20 shrink-0 rounded-full" />
    </HStack>
  );
}
